/**
 * Session-scoped cache for the Receiving queue (stale-while-revalidate).
 *
 * The queue endpoints (GET /api/v1/shipments + GET /api/v1/po-received-logs)
 * are heavy, so the page shows a full-screen spinner for several seconds on
 * every visit. This cache stores the last successfully built queue result so
 * the next visit paints instantly from cache while a fresh load runs in the
 * background. Only the default (unfiltered) first page is cached — filtered /
 * searched loads always hit the network as before.
 *
 * sessionStorage is used deliberately: the cache dies with the browser tab,
 * so no stale data survives across sessions, and nothing changes server-side.
 */

const CACHE_PREFIX = 'eadin:receiving-queue:v1:';
const MAX_AGE_MS = 10 * 60 * 1000; // hard TTL — older entries are ignored
const FRESH_AGE_MS = 60 * 1000; // entries younger than this skip the refetch spinner entirely

export function queueCacheKey(filters = {}) {
  const search = (filters.search ?? '').trim();
  const statusFilter = filters.statusFilter ?? 'all';
  const showClosed = filters.showClosed ?? false;
  const page = filters.page ?? 1;
  return `${CACHE_PREFIX}${JSON.stringify({ search, statusFilter, showClosed, page })}`;
}

export function readQueueCache(filters = {}) {
  try {
    const raw = sessionStorage.getItem(queueCacheKey(filters));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.pos)) return null;
    const age = Date.now() - (parsed.savedAt || 0);
    if (age > MAX_AGE_MS) return null;
    return {
      pos: parsed.pos,
      pagination: parsed.pagination || null,
      savedAt: parsed.savedAt,
      isFresh: age <= FRESH_AGE_MS,
    };
  } catch (err) {
    return null;
  }
}

export function writeQueueCache(filters = {}, result) {
  if (!result || !Array.isArray(result.pos)) return;
  try {
    sessionStorage.setItem(
      queueCacheKey(filters),
      JSON.stringify({
        pos: result.pos,
        pagination: result.pagination || null,
        savedAt: Date.now(),
      }),
    );
  } catch (err) {
    // Quota exceeded or unavailable storage — silently skip caching.
    try {
      clearQueueCache();
    } catch (e) {
      /* noop */
    }
  }
}

export function clearQueueCache() {
  try {
    const stale = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) stale.push(key);
    }
    stale.forEach((key) => sessionStorage.removeItem(key));
  } catch (err) {
    /* noop */
  }
}
